import { ChevronDown } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'

import { selectTriggerVariants } from '@/components/ui/select-trigger-variants'
import { cn } from '@/lib/utils'

interface SelectTriggerProps extends Omit<ComponentProps<'button'>, 'children'> {
  /** The whole visible text: "All sectors" when quiet, "Sectors · 3 of 11" when not. */
  label: string
  /** Flips the caret and lights the edge. Owned by whoever owns the menu's open state. */
  open?: boolean
  /** A selection is active, so the summary reads in accent. */
  filled?: boolean
  /** `chip` is the filter pill; `slot` is Compare's dashed "Add broker" card. */
  variant?: 'chip' | 'slot'
  /** Before the label — Compare's slot draws a plus. */
  leading?: ReactNode
  /**
   * Trailing caret. Most triggers open a menu whose own state the caret
   * flips, so this defaults on; Holdings' Filter and Columns triggers pass
   * `false` — their frame (7.png) draws a bare icon + label with no caret at
   * all, so there is no state left for a caret to flip.
   */
  chevron?: boolean
  /**
   * How the edge lights while the menu is open (sz:kit). `strong` (default): the strong hairline,
   * as hover. `accent`: the gold edge (the stock page's FY / Q select, mock `stc-select`). `line`:
   * the accent's hairline tint (the Signal run picker, `sz-select`).
   * A filled trigger keeps its own accent edge whatever this says.
   */
  openTone?: 'strong' | 'accent' | 'line'
}

const OPEN_STATE = { strong: 'open', accent: 'openAccent', line: 'openLine' } as const

/**
 * The button a menu hangs off. Pair it with Popover, which supplies the ref,
 * the click handler and the `aria-expanded` / `aria-controls` pair — spread
 * those straight onto this.
 *
 * Open and Filled are separate props rather than one state because they
 * combine: a filled trigger that is open keeps its accent summary and still
 * flips its caret. Figma's three-state axis is the flattening of that.
 */
export default function SelectTrigger({
  label,
  open = false,
  filled = false,
  variant = 'chip',
  leading,
  chevron = true,
  openTone = 'strong',
  className,
  ...props
}: SelectTriggerProps) {
  return (
    <button
      type="button"
      className={cn(
        selectTriggerVariants({
          variant,
          state: filled ? 'filled' : open ? OPEN_STATE[openTone] : 'resting',
        }),
        className,
      )}
      {...props}
    >
      {leading}
      {/* The slot spans a whole card, so its caret is pinned to the far edge
          by the label rather than sitting next to it as the chip's does. */}
      <span
        className={cn('min-w-0 truncate', variant === 'slot' ? 'flex-1 text-left' : null)}
        title={label}
      >
        {label}
      </span>
      {chevron ? (
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'size-4 shrink-0 transition-transform duration-med ease-out',
            open ? 'rotate-180' : null,
          )}
        />
      ) : null}
    </button>
  )
}
